import { Component } from 'react';
import moment from 'moment';
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View
} from '@react-pdf/renderer';
import Cookies from 'js-cookie';

//Files in the same folder
import PDFTable from './PDFTable';
import PDFTableHead from './PDFTableHead';
import PDFTableBody from './PDFTableBody';
import PDFTableRow from './PDFTableRow';
import PDFTableCell from './PDFTableCell';
// Types
import { Series, SeriesField } from './../../../../../core/types/Series';
// Utils
import { GetCurrencySymbol } from '../../../../../core/utils/GetCurrenciesData';
import GetBasicDetails from '../../../../../core/utils/GetBasicDetails';
// Constants
import {
  CDSA_REFERRING_VALUE,
  FCG_REFERRING_VALUE,
  SSO_CDH_COMPANY_KEY,
  TORFXOZ_REFERRING_VALUE,
  TORFX_REFERRING_VALUE
} from '../../../../../core/utils/Constants/Constants';

const styles = StyleSheet.create({

  bold: {
    fontWeight: 'bold',
    marginRight: '3px'
  },
  copyright: {
    backgroundColor: '#FAFAFA',
    padding: '15px'
  },
  footer: {
    fontSize: '10px'
  },
  generated: {
    display: 'flex',
    flexDirection: 'row'
  },
  header: {
    alignItem: 'center',
    display: 'flex',
    marginBottom: '20px'
  },
  logo: {
    height: 35,
    width: 127
  },
  torfxlogo: {
    height: 30,
    width: 127
  },
  fcglogo: {
    height: 41,
    width: 61,
    marginTop: 5,
    marginLeft: 5
  },
  page: {
    color: '#2C454C',
    padding: '30px'
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    padding: '10px'
  },
  title: {
    fontSize: '22px',
    fontWeight: 'heavy',
    marginTop: '20px',
    color: '#4A4A4A'
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1
  },
  sectionWrapper: {
    flexDirection: 'row'
  },
  companyName: {
    fontSize: '18px',
    fontWeight: 'heavy',
    color: '#000000'
  },
  affiliateName: {
    fontSize: '10px',
    color: '#4A4A4A'
  },
  affiliateNumber: {
    fontSize: '10px',
    color: '#4A4A4A'
  },
  affiliateDetailsWrapper: {
    backgroundColor: '#fafafa',
    padding: '15px',
    lineHeight: '1.5'
  },
  tableCell: {
    backgroundColor: '#fafafa',
    color: '#2C454C'
  }
});

interface APSeriesTableProps {
  logo: string,
  org?: string,
  groups?: Object,
  series?: Array<Series>,
  title: string,
  translate: Function,
  viewType?: string,
  affiliateData?: any,
  statementMonth?: string
  commissionDetails?: any
}

class PDFSeriesTable extends Component<APSeriesTableProps, {}> {
  render() {
    const {
      groups,
      logo,
      series,
      title,
      translate,
      viewType,
      affiliateData,
      statementMonth,
      commissionDetails
    } = this.props;

    if (!series && !groups) {
      return null;
    }
    const { referringCompany } = GetBasicDetails();
    const isTorLogo = referringCompany === TORFX_REFERRING_VALUE ||
      referringCompany === TORFXOZ_REFERRING_VALUE;
    const isFcgLogo = referringCompany === FCG_REFERRING_VALUE;
    const selectedLogoClass = isFcgLogo ? styles.fcglogo : (isTorLogo ? styles.torfxlogo : styles.logo);

    let resolution: any = groups;
    if (!resolution) {
      resolution = {
        'generic': {
          series
        }
      }
    }
    let cdhCompany: any = Cookies.get(SSO_CDH_COMPANY_KEY) ?? Cookies.get('cdhCompany');
    const isCDSAReferringCompany: boolean = cdhCompany === CDSA_REFERRING_VALUE;
    const isTorFXOzReferringCompany: boolean = cdhCompany === TORFXOZ_REFERRING_VALUE;

    const showFooterLine3 = isCDSAReferringCompany;

    const tierAmountCurrencySymbol: string = GetCurrencySymbol(commissionDetails?.TierAmountCurrency);
    const totalCommissionCurrencySymbol: string = GetCurrencySymbol(commissionDetails?.TotalCommissionCurrency);
    const commPaidCurrencySymbol: string = GetCurrencySymbol(commissionDetails?.CommPaidCurrency);

    let detailedViewTableFirstRowLabels: Array<string> = [],
      detailedViewTableSecondRowLabels: Array<string> = [],
      summaryViewTableFirstRowLabels: Array<string> = [],
      detailedViewTableFirstRowValueObj: any = {},
      detailedViewTableSecondRowValueObj: any = {},
      summaryViewTableFirstRowValuesObj: any = {};

    if (commissionDetails) {

      detailedViewTableFirstRowLabels = [
        'commissions.section.detail.column.CommPaidDate',
        'commissions.section.detail.column.CommPaidCurrency',
        'commissions.section.detail.column.CommRate',
        'commissions.section.detail.column.TotalClosedContracts',
        'commissions.section.detail.column.NewTradingClients'];
      detailedViewTableSecondRowLabels = [
        'commissions.tiers.first.label',
        'commissions.tiers.second.label',
        'commissions.tiers.third.label',
        'commissions.section.detail.column.TotalCommissionAmount',
        'commissions.section.detail.column.TotalCommissionAmountPaid'];
      summaryViewTableFirstRowLabels = [
        'commissions.section.detail.column.CommPaidDate',
        'commissions.section.detail.column.CommPaidCurrency',
        'commissions.section.detail.column.CommRate',
        'commissions.section.detail.column.TotalClosedContracts',
        'commissions.section.detail.column.NewTradingClients',
        'commissions.section.detail.column.TotalCommissionAmount',
        'commissions.section.detail.column.TotalCommissionAmountPaid'];

      detailedViewTableFirstRowValueObj = {
        'Paid date': commissionDetails.CommPaidDate ? commissionDetails.CommPaidDate : '',
        'Paid currency': commissionDetails.CommPaidCurrency ? commissionDetails.CommPaidCurrency : '',
        'Rate': commissionDetails.CommRate ? commissionDetails.CommRate : '',
        'Total closed contracts': commissionDetails.TotalClosedContracts ? commissionDetails.TotalClosedContracts : '',
        'New trading clients': commissionDetails.NewTradingClients ? commissionDetails.NewTradingClients : ''
      };

      detailedViewTableSecondRowValueObj = {
        'First tier': commissionDetails.FirstTierCommissionAmount ?
          `${tierAmountCurrencySymbol}${commissionDetails.FirstTierCommissionAmount}` : '',
        'Second tier': commissionDetails.SecondTierCommissionAmount ?
          `${tierAmountCurrencySymbol}${commissionDetails.SecondTierCommissionAmount}` : '',
        'Third tier': commissionDetails.ThirdTierCommissionAmount ?
          `${tierAmountCurrencySymbol}${commissionDetails.ThirdTierCommissionAmount}` : '',
        'Total commission': commissionDetails.TotalCommissionAmount ?
          `${totalCommissionCurrencySymbol}${commissionDetails.TotalCommissionAmount}` : '',
        'Total amount paid': commissionDetails.TotalCommissionAmountPaid ?
          `${commPaidCurrencySymbol}${commissionDetails.TotalCommissionAmountPaid}` : ''
      };

      summaryViewTableFirstRowValuesObj = {
        'Paid date': commissionDetails.CommPaidDate ? commissionDetails.CommPaidDate : '',
        'Paid currency': commissionDetails.CommPaidCurrency ? commissionDetails.CommPaidCurrency : '',
        'Rate': commissionDetails.CommRate ? commissionDetails.CommRate : '',
        'Total closed contracts': commissionDetails.TotalClosedContracts ? commissionDetails.TotalClosedContracts : '',
        'New trading clients': commissionDetails.NewTradingClients ? commissionDetails.NewTradingClients : '',
        'Total commission': commissionDetails.TotalCommissionAmount ?
          `${totalCommissionCurrencySymbol}${commissionDetails.TotalCommissionAmount}` : '',
        'Total amount paid': commissionDetails.TotalCommissionAmountPaid ?
          `${commPaidCurrencySymbol}${commissionDetails.TotalCommissionAmountPaid}` : ''
      };

    }

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page} wrap>
          <View>
            <View style={styles.sectionWrapper}>
              <View style={styles.section}>
                <View style={styles.header}>
                  <Image src={logo} style={selectedLogoClass} />
                  <Text style={styles.title}>
                    {title}
                  </Text>
                </View>
              </View>
              <View style={styles.section}>
                <View style={styles.affiliateDetailsWrapper}>
                  <Text style={styles.companyName}>{affiliateData?.company_name}</Text>
                  {/* <Text style={styles.affiliateName}>{(affiliateData?.user_first_name)} {(affiliateData?.user_last_name)}</Text> */}
                  <Text style={styles.affiliateNumber}>{translate('pdf.partner.id.label')}{affiliateData?.affiliate_Number}</Text>
                </View>
              </View>
            </View>
            <View>
              {commissionDetails && viewType === 'Detailed' && (
                <PDFTable>
                  <PDFTableHead>
                    <PDFTableRow>
                      <PDFTableCell>
                        <Text>{translate('pdf.statement.month.label')}<span style={styles.tableCell}> {statementMonth} </span></Text>
                      </PDFTableCell>
                    </PDFTableRow>

                    <PDFTableRow>
                      {detailedViewTableFirstRowLabels.map((columnName: string, index: number) => (
                        <PDFTableCell key={index}>
                          <Text>{columnName}</Text>
                        </PDFTableCell>
                      ))}
                    </PDFTableRow>

                  </PDFTableHead>
                  <PDFTableBody>
                    <PDFTableRow>
                      {Object.values(detailedViewTableFirstRowValueObj).map((values: any) => {
                        return (
                          <PDFTableCell>
                            <Text style={styles.tableCell}>{values}</Text>
                          </PDFTableCell>
                        );
                      })}

                    </PDFTableRow>
                  </PDFTableBody>
                  <PDFTableHead>
                    <PDFTableRow>
                      {detailedViewTableSecondRowLabels.map((columnName: string, index: number) => (
                        <PDFTableCell key={index}>
                          <Text>{columnName}</Text>
                        </PDFTableCell>
                      ))}
                    </PDFTableRow>

                  </PDFTableHead>
                  <PDFTableBody>
                    <PDFTableRow>
                      {Object.values(detailedViewTableSecondRowValueObj).map((values: any) => {
                        return (
                          <PDFTableCell>
                            <Text style={styles.tableCell}>{values}</Text>
                          </PDFTableCell>
                        );
                      })}

                    </PDFTableRow>
                  </PDFTableBody>
                </PDFTable>
              )}
              {commissionDetails && viewType === 'Summary' && (
                <PDFTable>
                  <PDFTableHead>
                    <PDFTableRow>
                      <PDFTableCell>
                        <Text>{translate('pdf.statement.month.label')}<span style={styles.tableCell}> {statementMonth} </span></Text>
                      </PDFTableCell>
                    </PDFTableRow>
                    <PDFTableRow>
                      {summaryViewTableFirstRowLabels.map((columnName: string, index: number) => (
                        <PDFTableCell key={index}>
                          <Text>{translate(columnName)}</Text>
                        </PDFTableCell>
                      ))}
                    </PDFTableRow>
                  </PDFTableHead>
                  <PDFTableBody>
                    <PDFTableRow>
                      {Object.values(summaryViewTableFirstRowValuesObj).map((values: any) => {
                        return (
                          <PDFTableCell>
                            <Text style={styles.tableCell}>{values}</Text>
                          </PDFTableCell>
                        );
                      })}
                    </PDFTableRow>
                  </PDFTableBody>
                </PDFTable>
              )}
            </View>
            {Object.keys(resolution).map((key: string) => {
              const group = resolution[key];
              const wideEnableFields = ['uid', 'deal_no', 'customer_id'];
              if (group.series && group.series.length > 0) {
                return (
                  <View>
                    {group.title && (
                      <Text style={styles.subtitle}>
                        {group.title}
                      </Text>
                    )}
                    <PDFTable>
                      <PDFTableHead>
                        <PDFTableRow>
                          {viewType === 'accountView' &&
                            <PDFTableCell>
                              <Text>{translate('series.table.month.label')}</Text>
                            </PDFTableCell>}
                          {group.series && group.series.length > 0 && group.series[0]?.fields.map((column: SeriesField) => (
                            <PDFTableCell key={column.fieldName} wide={!!column.customAttributes || wideEnableFields.includes(column.fieldName)}>
                              <Text>{column.fieldLabel}</Text>
                            </PDFTableCell>
                          ))}
                        </PDFTableRow>
                      </PDFTableHead>
                      <PDFTableBody>
                        {group.series.map((event: any, index: number) => (
                          <PDFTableRow>
                            {viewType === 'accountView' &&
                              <PDFTableCell alternative={(index % 2)}>
                                <Text>{event.event.eventLabel}</Text>
                              </PDFTableCell>}
                            {event.fields.map((column: SeriesField) => (
                              <PDFTableCell
                                key={`${event.event.eventLabel}-${column.fieldName}`}
                                alternative={(index % 2) === 0}
                                field={column}
                                wide={!!column.customAttributes || wideEnableFields.includes(column.fieldName)}
                              />
                            ))}
                          </PDFTableRow>
                        ))}
                      </PDFTableBody>
                    </PDFTable>
                  </View>
                )
              }
              return null;
            })}
            <View style={styles.footer}>
              <View style={styles.generated}>
                <Text style={styles.bold}>{translate('pdf.generated.label')}:</Text><Text>{moment().format('Do MMMM YYYY')}</Text>
              </View>
              {
                isTorFXOzReferringCompany ?
                  <View style={styles.copyright}>
                    <Text>{translate('footer.copyright.line1')}</Text>
                    <Text>{translate('footer.copyright.line2')}</Text>
                    <Text>{translate('footer.copyright.line3')}</Text>
                    <Text>{translate('footer.copyright.line4')}</Text>
                  </View>
                  :
                  <View style={styles.copyright}>
                    <Text>{translate('footer.copyright.line1')}{translate('footer.copyright.line2')}{showFooterLine3 && translate('footer.copyright.line3')}</Text>
                  </View>
              }
            </View>
          </View>
        </Page>
      </Document>
    );
  }
}

export default PDFSeriesTable
